/**
 * LeadCardList Component - Wrapper for displaying lead cards
 * Handles empty states, loading, and responsive layout
 */

import React from "react";
import { Box, Typography, CircularProgress, Grid } from "@mui/material";
import LeadCard from "./LeadCard.jsx";
import { logError } from "@/shared/utils/logger.js";

/**
 * LeadCardList Component
 * @param {Object} props
 * @param {Array} props.leads - Array of lead objects (can be grouped or ungrouped)
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onWhatsApp - Callback when WhatsApp button clicked
 * @param {Function} props.onQuote - Callback when Quote button clicked
 * @param {Function} props.onFollowUp - Callback when Follow-up button clicked
 * @param {Function} props.onView - Callback when card is clicked
 * @param {Function} props.onAccept - Callback when accepting lead
 * @param {Function} props.onReject - Callback when rejecting lead
 * @param {Function} props.onRoute - Callback when route button clicked
 * @param {boolean} props.isTablet - Whether to show 2-column layout (tablet)
 */
const LeadCardList = ({
  leads = [],
  loading = false,
  onWhatsApp,
  onQuote,
  onFollowUp,
  onView,
  onAccept,
  onReject,
  onRoute,
  isTablet = false,
}) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "48px 16px",
        }}
      >
        <CircularProgress />
        <Typography
          variant="body2"
          sx={{ marginTop: 2, color: "rgba(0, 0, 0, 0.6)" }}
        >
          Loading leads...
        </Typography>
      </Box>
    );
  }

  if (!leads || leads.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "48px 16px",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="textSecondary">
          No leads found
        </Typography>
        <Typography
          variant="body2"
          sx={{ marginTop: 2, color: "rgba(0, 0, 0, 0.6)" }}
        >
          Try adjusting your filters or create a new lead.
        </Typography>
      </Box>
    );
  }

  // Handle grouped leads (with count property) vs regular leads
  const renderLeads = () => {
    return leads.map((leadOrGroup, index) => {
      // Check if this is a grouped lead (has count property)
      const isGrouped = leadOrGroup.count !== undefined;
      const lead = isGrouped ? leadOrGroup.primaryLead : leadOrGroup;
      const count = isGrouped ? leadOrGroup.count : undefined;

      if (!lead) {
        logError("Invalid lead data in LeadCardList", { index, leadOrGroup });
        return null;
      }

      return (
        <Grid
          item
          xs={12}
          md={isTablet ? 6 : 12}
          key={lead._id || lead.id || index}
        >
          <LeadCard
            lead={lead}
            count={count}
            onWhatsApp={onWhatsApp}
            onQuote={onQuote}
            onFollowUp={onFollowUp}
            onView={onView}
            onAccept={onAccept}
            onReject={onReject}
            onRoute={onRoute}
          />
        </Grid>
      );
    });
  };

  return (
    <Box sx={{ padding: "16px 0" }}>
      <Grid container spacing={2}>
        {renderLeads()}
      </Grid>
    </Box>
  );
};

export default LeadCardList;
